import * as Yup from "yup";
import { TemplateSettingsForm } from "../types/forms";
import { siteSettings } from "../settings/site-settings";

export const createTranslationSchema = (
  englishOptional = false,
  fieldName?: string
) => {
  return Yup.object().shape(
    siteSettings.translatedInputs.supportedLanguages.reduce(
      (schema: { [key: string]: Yup.StringSchema }, lang: string) => {
        if (
          lang === siteSettings.translatedInputs.defaultLanguage &&
          !englishOptional
        ) {
          schema[lang] = Yup.string().required(
            `${fieldName ?? "This field"} is required in ${lang.toUpperCase()}`
          );
        } else {
          // Other languages are optional
          schema[lang] = Yup.string().optional();
        }
        return schema;
      },
      {}
    )
  );
};

export const validationSchemas = {
  login: Yup.object().shape({
    email: Yup.string().email().required(),
    password: Yup.string().required(),
  }),

  projectCreate: Yup.object().shape({
    name: Yup.string().required(),
    slug: Yup.string().required(),
  }),

  projectUpdate: Yup.object().shape({
    name: Yup.string().required(),
    slug: Yup.string().required(),
  }),

  printLocationCreate: Yup.object().shape({
    internalName: Yup.string().required(),
    slug: Yup.string().required(),
  }),

  printLocationUpdate: Yup.object().shape({
    internalName: Yup.string().required(),
  }),

  templateSettings: Yup.object().shape({
    internalName: Yup.string().required("Internal name is required"),
    slug: Yup.string().required("Slug is required"),
    description: createTranslationSchema(true, "Description"),
    displayName: createTranslationSchema(false, "Display Name"),
    isPublished: Yup.boolean().required("Published status is required"),
    fitCategory: Yup.object().shape({
      light: Yup.boolean(),
      dark: Yup.boolean(),
    }),

    tags: Yup.array().of(Yup.string()).optional(),
    projectIds: Yup.array().of(Yup.string()).optional(),
    categorySelections: Yup.array().of(
      Yup.object()
        .shape({
          categoryId: Yup.string().required(), // required at least to have parent.
          subcategoryIds: Yup.array().of(Yup.string()).optional(), // optional
        })
        .optional()
    ),

    printLocations: Yup.array()
      .of(
        Yup.object().shape({
          location: Yup.string().required(),
          default: Yup.boolean().required(),
        }) // required
      )
      .optional(),

    fetchOnlyWithinSets: Yup.boolean().optional(),
    sharedCustomization: Yup.boolean().optional(),
  }),

  createSet: Yup.object().shape({
    internalName: Yup.string().required("Internal name is required"),
    slug: Yup.string().required("Slug is required"),
    description: createTranslationSchema(true, "Description"),
    displayName: createTranslationSchema(false, "Display Name"),
    isPublished: Yup.boolean().required("Published status is required"),
    templateLocationLinks: Yup.array()
      .of(
        Yup.object().shape({
          templateId: Yup.string().required(),
          printLocation: Yup.string().required(),
          order: Yup.number().optional(),
        })
      )
      .min(2, "At least 2 templates are required to form a set"),
  }),

  categoryCreate: Yup.object().shape({
    displayName: createTranslationSchema(false, "Display Name"),
    internalName: Yup.string().required("Internal name is required"),
    slug: Yup.string().required("Slug is required"),
    image: Yup.string().optional(),
    description: createTranslationSchema(true, "Description"),
  }),

  subcategoryCreate: Yup.object().shape({
    parentIds: Yup.array().of(Yup.string()).optional(),
    displayName: createTranslationSchema(false, "Display Name"),
    internalName: Yup.string().required("Internal name is required"),
    slug: Yup.string().required("Slug is required"),
    image: Yup.string().optional(),
    description: createTranslationSchema(true, "Description"),
  }),
};

export const initialValues = {
  login: {
    email: "",
    password: "",
  },

  projectUpdate: (project: any) => {
    return {
      name: project?.name ?? "",
      slug: project?.slug ?? "",
    };
  },

  printLocationCreate: (printLocation: any) => {
    return {
      internalName: "",
      slug: "",
    };
  },

  printLocationUpdate: (printLocation: any) => {
    return {
      internalName: printLocation?.internalName ?? "",
    };
  },

  categoryUpdate: (category: any) => {
    return {
      displayName: category?.displayName ?? { en: "" },
      internalName: category?.internalName ?? "",
      slug: category?.slug ?? "",
      image: category?.image ?? "",
      description: category?.description ?? { en: "" },
    };
  },

  subcategoryUpdate: (subcategory: any) => {
    return {
      parentIds: subcategory?.parentIds ?? [],
      displayName: subcategory?.displayName ?? { en: "" },
      internalName: subcategory?.internalName ?? "",
      slug: subcategory?.slug ?? "",
      image: subcategory?.image ?? "",
      description: subcategory?.description ?? { en: "" },
    };
  },

  templateCreateOrUpdate: (templateSettings: TemplateSettingsForm) => {
    return {
      internalName: templateSettings.internalName,
      slug: templateSettings.slug,
      description: templateSettings.description || { en: "" },
      displayName: templateSettings.displayName || { en: "" },
      fitCategory: templateSettings.fitCategory || {
        light: true,
        dark: true,
      },
      isPublished: templateSettings.isPublished,
      tags: templateSettings.tags || [],
      projectIds: templateSettings.projectIds || [],
      categorySelections: templateSettings.categorySelections || [],
      printLocations: templateSettings.printLocations || [],
      fetchOnlyWithinSets: templateSettings.fetchOnlyWithinSets || false,
      sharedCustomization: templateSettings.sharedCustomization || false,
    };
  },

  setCreateOrUpdate: (set: any) => {
    return {
      internalName: set?.internalName ?? "",
      slug: set?.slug ?? "",
      description: set?.description || { en: "" },
      displayName: set?.displayName || { en: "" },
      templateLocationLinks: set?.templateLocationLinks || [],
      isPublished: set?.isPublished ?? false,
    };
  },
};
